import { useState, useEffect } from 'react'
import { alertsService } from '../services/alertsService'

const today = () => new Date().toISOString().split('T')[0]

const createDefaultAlert = () => ({
    enabled: false,
    message: '',
    type: 'service_outage',
    startDate: today(),
    startTime: '00:00',
    endDate: '',
    endTime: '23:59',
    timezone: 'UTC',
})

/**
 * Alerts Page - Enterprise Standard
 * System-wide broadcast management.
 */
export default function Alerts() {
    const [hotAlert, setHotAlert] = useState(createDefaultAlert)
    const [history, setHistory] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [status, setStatus] = useState(null)

    useEffect(() => {
        loadAlert()
    }, [])

    const loadAlert = async () => {
        setLoading(true)
        try {
            const [data, alertHistory] = await Promise.all([
                alertsService.getHotAlert(),
                alertsService.getHotAlertHistory(),
            ])
            setHotAlert(data)
            setHistory(alertHistory)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const savedAlert = await alertsService.updateHotAlert(hotAlert)
            setHotAlert(savedAlert)
            setHistory(await alertsService.getHotAlertHistory())
            setStatus('saved')
            setTimeout(() => setStatus(null), 3000)
        } catch (err) {
            setStatus('error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <h1 className="text-2xl font-bold text-admin-heading tracking-tight">System Alerts</h1>

            {/* Main Card */}
            <div className="max-w-3xl card-admin">
                <div className="px-6 py-4 border-b border-admin-border bg-gray-50 flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-bold text-admin-heading">Global "Hot Alert" Banner</h2>
                        <p className="text-xs text-admin-muted mt-0.5">Displays a Dismissible banner at the top of all user dashboards.</p>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Admin Guidance */}
                    <div className="p-4 bg-status-info-bg border border-status-info/20 rounded-lg flex items-start gap-3">
                        <svg className="w-5 h-5 text-status-info mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>
                            <p className="text-sm font-bold text-status-info">Administrative Notice</p>
                            <p className="text-xs text-status-info/80 mt-1">This banner appears globally for all users. Please ensure you <span className="font-bold underline">disable this alert</span> immediately once the issue is resolved to maintain platform trust.</p>
                        </div>
                    </div>

                    {/* Toggle */}
                    <div className="flex items-center justify-between p-4 bg-white border border-admin-border rounded-lg shadow-sm">
                        <div>
                            <p className="text-sm font-bold text-admin-heading">Alert Live</p>
                            <p className="text-xs text-admin-muted">Enable to broadcast to all users immediately.</p>
                        </div>
                        <button
                            onClick={() => setHotAlert(p => ({ ...p, enabled: !p.enabled }))}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-navy focus:ring-offset-2 ${hotAlert.enabled ? 'bg-status-error' : 'bg-gray-200'}`}
                        >
                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${hotAlert.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>

                    {/* Alert Settings Grid */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                            <label className="label-admin">Alert Type</label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <select
                                        value={['service_outage', 'data_issue', 'legal_notice'].includes(hotAlert.type) ? hotAlert.type : 'custom'}
                                        onChange={(e) => {
                                            if (e.target.value === 'custom') {
                                                setHotAlert(p => ({ ...p, type: '' }))
                                            } else {
                                                setHotAlert(p => ({ ...p, type: e.target.value }))
                                            }
                                        }}
                                        className="select-admin w-full"
                                    >
                                        <option value="service_outage">Service Outage</option>
                                        <option value="data_issue">Critical Data Issue</option>
                                        <option value="legal_notice">Legal or Operational Notice</option>
                                        <option value="custom">Custom / Other...</option>
                                    </select>
                                </div>
                                {(!['service_outage', 'data_issue', 'legal_notice'].includes(hotAlert.type)) && (
                                    <input
                                        type="text"
                                        placeholder="Enter custom type..."
                                        value={hotAlert.type}
                                        onChange={(e) => setHotAlert(p => ({ ...p, type: e.target.value }))}
                                        className="input-admin animate-fade-in"
                                        autoFocus
                                    />
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="label-admin">Start Date</label>
                            <input
                                type="date"
                                value={hotAlert.startDate}
                                onChange={(e) => setHotAlert(p => ({ ...p, startDate: e.target.value }))}
                                className="input-admin"
                            />
                        </div>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="label-admin">Start Time</label>
                                <input
                                    type="time"
                                    value={hotAlert.startTime}
                                    onChange={(e) => setHotAlert(p => ({ ...p, startTime: e.target.value }))}
                                    className="input-admin"
                                />
                            </div>
                            <div className="w-32">
                                <label className="label-admin">Zone</label>
                                <div className="relative">
                                    <select
                                        value={hotAlert.timezone}
                                        onChange={(e) => setHotAlert(p => ({ ...p, timezone: e.target.value }))}
                                        className="select-admin w-full"
                                    >
                                        {[
                                            'UTC', 'GMT', 'EST', 'CST', 'MST', 'PST',
                                            'Europe/London', 'Europe/Paris', 'Europe/Berlin',
                                            'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
                                            'Australia/Sydney'
                                        ].map(tz => (
                                            <option key={tz} value={tz}>{tz}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="label-admin">End Date</label>
                            <input
                                type="date"
                                value={hotAlert.endDate}
                                onChange={(e) => setHotAlert(p => ({ ...p, endDate: e.target.value }))}
                                className="input-admin"
                            />
                        </div>
                        <div>
                            <label className="label-admin">End Time</label>
                            <input
                                type="time"
                                value={hotAlert.endTime}
                                onChange={(e) => setHotAlert(p => ({ ...p, endTime: e.target.value }))}
                                className="input-admin"
                            />
                        </div>
                    </div>

                    {/* Message Input */}
                    <div>
                        <label className="label-admin">Alert Content</label>
                        <textarea
                            value={hotAlert.message}
                            onChange={(e) => setHotAlert(p => ({ ...p, message: e.target.value }))}
                            rows={3}
                            className="input-admin"
                            placeholder="Briefly describe the impact and estimated resolution time..."
                        />
                        <div className="mt-1 flex justify-between">
                            <span className="text-xs text-admin-muted">Visible to all users.</span>
                            <span className="text-xs text-admin-muted">{hotAlert.message.length}/500</span>
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="flex items-center justify-between pt-4 border-t border-admin-border">
                        <div>
                            {status === 'saved' && <span className="text-sm text-status-success font-medium animate-fade-in">Changes saved successfully.</span>}
                            {status === 'error' && <span className="text-sm text-status-error font-medium animate-fade-in">Failed to save changes.</span>}
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setHotAlert(p => ({ ...p, message: '' }))}
                                className="btn-secondary"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="btn-primary"
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tracking History */}
            <div className="card-admin">
                <div className="px-6 py-4 border-b border-admin-border bg-gray-50 flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-bold text-admin-heading">Tracking History</h2>
                        <p className="text-xs text-admin-muted mt-0.5">Recent alert changes, scheduled windows, and ownership.</p>
                    </div>
                    <button
                        onClick={loadAlert}
                        disabled={loading}
                        className="btn-secondary text-xs"
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-admin-border">
                        <thead className="bg-gray-50 text-admin-muted">
                            <tr>
                                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wider">Message</th>
                                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wider">Start</th>
                                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wider">End</th>
                                <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wider">Updated By</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-admin-border">
                            {history.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-sm text-admin-muted text-center">
                                        No alert history yet.
                                    </td>
                                </tr>
                            )}
                            {history.map((alert) => (
                                <tr key={alert.id || `${alert.createdAt}-${alert.message}`} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <AlertStatus enabled={alert.enabled} endDate={alert.endDate} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-admin-heading">
                                        {formatAlertType(alert.type)}
                                    </td>
                                    <td className="px-6 py-4 min-w-72 max-w-xl">
                                        <p className="text-sm text-admin-heading line-clamp-2">{alert.message || 'No message'}</p>
                                        <p className="text-xs text-admin-muted mt-1">Created {formatDateTime(alert.createdAt)}</p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-admin-muted">
                                        {formatSchedule(alert.startDate, alert.startTime, alert.timezone)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-admin-muted">
                                        {alert.endDate ? formatSchedule(alert.endDate, alert.endTime, alert.timezone) : 'No end date'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-admin-muted">
                                        {alert.createdByName || alert.createdByEmail || 'System'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

function formatAlertType(type) {
    const labels = {
        service_outage: 'Service Outage',
        data_issue: 'Critical Data Issue',
        legal_notice: 'Legal or Operational Notice',
    }
    return labels[type] || type || 'Custom'
}

function formatDateTime(value) {
    if (!value) return 'Unknown'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'Unknown'
    return date.toLocaleString()
}

function formatSchedule(date, time, timezone) {
    if (!date) return 'Not set'
    return `${date}${time ? ` ${time}` : ''}${timezone ? ` ${timezone}` : ''}`
}

function AlertStatus({ enabled, endDate }) {
    const ended = endDate && endDate < new Date().toISOString().split('T')[0]
    const label = enabled && !ended ? 'Live' : ended ? 'Ended' : 'Inactive'
    const style = enabled && !ended
        ? 'bg-status-error-bg text-status-error border-status-error/20'
        : ended
            ? 'bg-gray-100 text-admin-muted border-admin-border'
            : 'bg-status-info-bg text-status-info border-status-info/20'

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wide border ${style}`}>
            {label}
        </span>
    )
}
